import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { useInquiry } from "../lib/InquiryContext";
import { usePrograms, resolveUrl } from "../lib/useCMS";
import { slugifyTitle } from "../lib/slug";
import { trackConversion, EVENTS } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InquiryModal = () => {
  const { open, program, closeInquiry } = useInquiry();
  const programs = usePrograms([]);

  const [form, setForm] = useState({ name: "", email: "", phone: "", program: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // a11y: capture the element that opened the modal so we can restore focus on close.
  const lastFocusedRef = useRef(null);
  const firstFieldRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Pre-populate program when modal opens
  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, program: program?.title || "" }));
      setSubmitted(false);
      setError("");
    }
  }, [open, program]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeInquiry();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeInquiry]);

  // a11y: focus management. On open, remember the trigger element and move
  // focus into the modal (first input). On close, restore focus to the
  // element that opened it so screen-reader / keyboard users don't get lost.
  useEffect(() => {
    if (open) {
      lastFocusedRef.current = document.activeElement;
      // Defer one tick so the modal nodes are mounted.
      const t = setTimeout(() => {
        if (firstFieldRef.current) firstFieldRef.current.focus();
        else if (closeBtnRef.current) closeBtnRef.current.focus();
      }, 50);
      return () => clearTimeout(t);
    }
    // closing — restore focus
    const prev = lastFocusedRef.current;
    if (prev && typeof prev.focus === "function") {
      prev.focus();
    }
  }, [open]);

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const matched = programs.find((p) => p.title === form.program);
      await axios.post(`${API}/cms/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        program: form.program.trim(),
        program_slug: matched?.slug || (form.program ? slugifyTitle(form.program) : ""),
        message: form.message.trim(),
        source_path: typeof window !== "undefined" ? window.location.pathname : "",
      });
      setSubmitted(true);
      trackConversion(EVENTS.INQUIRY_SUBMIT, {
        program: form.program.trim() || undefined,
        program_slug: matched?.slug || undefined,
        source_path: typeof window !== "undefined" ? window.location.pathname : undefined,
      });
      setForm({ name: "", email: "", phone: "", program: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm"
            onClick={closeInquiry}
            data-testid="inquiry-backdrop"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="fixed inset-0 z-[61] grid place-items-center p-4 sm:p-6 pointer-events-none"
            role="dialog"
            aria-modal="true"
            aria-labelledby="inquiry-modal-title"
            data-testid="inquiry-modal"
          >
            <div className="pointer-events-auto w-full max-w-[640px] max-h-[92vh] overflow-y-auto bg-[#0c0c0c] border border-white/10 shadow-2xl relative">
              {/* Header with program imagery */}
              <div className="relative h-32 sm:h-40 overflow-hidden border-b border-white/10">
                {program?.image_url ? (
                  <img
                    src={resolveUrl(program.image_url)}
                    alt={program.title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover opacity-50"
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-brand to-black" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-black/60 to-transparent" />
                <button
                  type="button"
                  ref={closeBtnRef}
                  onClick={closeInquiry}
                  className="absolute top-4 right-4 h-10 w-10 grid place-items-center bg-black/60 border border-white/15 hover:border-brand-light hover:text-brand-light transition-colors"
                  aria-label="Close inquiry form"
                  data-testid="inquiry-close"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="absolute bottom-4 left-5 sm:left-6 right-16">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold">
                    {program?.tag || "Inquire"}
                  </div>
                  <h2 id="inquiry-modal-title" className="font-display uppercase text-2xl sm:text-3xl mt-1 leading-[1] text-white">
                    {submitted ? "Got it." : program?.title || "Inquire Now"}
                  </h2>
                </div>
              </div>

              <div className="p-6 sm:p-8">
                {submitted ? (
                  <div className="text-center py-6" data-testid="inquiry-success">
                    <div className="inline-flex h-14 w-14 items-center justify-center bg-brand mb-5">
                      <CheckCircle2 className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="font-display text-xl uppercase mb-3">Thanks for reaching out.</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed max-w-md mx-auto">
                      Coach Mike will get back to you within 1 business day with next steps for{" "}
                      <span className="text-white font-semibold">{program?.title || "your inquiry"}</span>.
                    </p>
                    <button
                      type="button"
                      onClick={closeInquiry}
                      className="btn-ghost !py-3 !px-5 !text-xs mt-7"
                      data-testid="inquiry-success-close"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-zinc-400 text-sm mb-5 leading-relaxed">
                      Tell us about the player. We'll come back with a plan and next steps.
                    </p>
                    <form onSubmit={onSubmit} className="space-y-4" data-testid="inquiry-form">
                      {/* Honeypot — hidden from humans, visible to bots. Must stay empty. */}
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={onField("website")}
                        className="hidden"
                        aria-hidden="true"
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Name" required>
                          <input
                            ref={firstFieldRef}
                            type="text"
                            required
                            value={form.name}
                            onChange={onField("name")}
                            className="form-input"
                            placeholder="Player or parent name"
                            data-testid="inquiry-name"
                            autoComplete="name"
                          />
                        </Field>
                        <Field label="Email" required>
                          <input
                            type="email"
                            required
                            value={form.email}
                            onChange={onField("email")}
                            className="form-input"
                            placeholder="you@example.com"
                            data-testid="inquiry-email"
                            autoComplete="email"
                          />
                        </Field>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Phone">
                          <input
                            type="tel"
                            value={form.phone}
                            onChange={onField("phone")}
                            className="form-input"
                            placeholder="(555) 555-1234"
                            data-testid="inquiry-phone"
                            autoComplete="tel"
                          />
                        </Field>
                        <Field label="Program" required>
                          <select
                            required
                            value={form.program}
                            onChange={onField("program")}
                            className="form-input pr-10 appearance-none"
                            data-testid="inquiry-program"
                          >
                            <option value="">Select a program</option>
                            {programs.map((p) => (
                              <option key={p.id || p.slug || p.title} value={p.title}>
                                {p.title}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>
                      <Field label="Additional Info">
                        <textarea
                          rows={3}
                          value={form.message}
                          onChange={onField("message")}
                          className="form-input"
                          placeholder="Player age / level, goals, timing, anything we should know"
                          data-testid="inquiry-message"
                          maxLength={2000}
                        />
                      </Field>

                      {error && (
                        <div className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2" data-testid="inquiry-error">
                          {error}
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row gap-3 pt-2">
                        <button
                          type="submit"
                          disabled={submitting}
                          className="btn-primary justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                          data-testid="inquiry-submit"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                            </>
                          ) : (
                            <>
                              Send Inquiry <ArrowUpRight className="h-5 w-5" />
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={closeInquiry}
                          className="btn-ghost justify-center !py-3 !px-5 !text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                      <p className="text-[10px] tracking-wide text-zinc-500 mt-2">
                        By submitting this form you agree to be contacted about your inquiry. See our{" "}
                        <a href="/privacy" className="underline hover:text-brand-light">Privacy Policy</a>.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-400 mb-1.5">
      {label} {required && <span className="text-brand-light">*</span>}
    </div>
    {children}
  </label>
);
